import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, Users, MapPin, Clock, Check, Star, Snowflake, Flame, TentTree, UtensilsCrossed, Music4, Trophy, Sparkles, Wine } from 'lucide-react';

// ============================================================
//  ZLINER · Firemní vánoční akce 2026
//  Client: Zliner s.r.o. (IVECO Bus dealer)
//  Agency: VŠE PRO AKCI s.r.o.
//  Style: Vánoční trhy · exteriér + interiér
// ============================================================

const LOGO_ZLINER = 'https://customer-assets-jai6qajn.emergentagent.net/job_vsepro-wanzl/artifacts/j7nmea9i_logo%20zliner.png';
const LOGO_VPA = 'https://customer-assets-jai6qajn.emergentagent.net/job_vsepro-wanzl/artifacts/4t7rchw6_VPA-logo-white.png';

const PHOTOS = {
  ofyrNight: 'https://customer-assets-jai6qajn.emergentagent.net/job_vsepro-wanzl/artifacts/7cdtqvhx_62f41f5e-cb35-4f62-8267-8005252005f3.JPG',
  foodTruck: 'https://customer-assets-jai6qajn.emergentagent.net/job_vsepro-wanzl/artifacts/iogl7gy0_8fe25603-b83d-4a4b-80d1-7fd58fbbae4c.JPG',
  tentInterior: 'https://customer-assets-jai6qajn.emergentagent.net/job_vsepro-wanzl/artifacts/k3hcvsq0_5ed93dd6-cae6-4c1c-8a73-18566423e883.JPG',
  tentsDay: 'https://customer-assets-jai6qajn.emergentagent.net/job_vsepro-wanzl/artifacts/0oh0r36i_01c2c47b-0915-4093-b41b-ed0769872305.JPG',
  stage1: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/9gyn0bcw_stage-8x6-v2-1.jpg',
  stage2: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/ay6nz6ma_kontakt%20fest%208x6.jpg',
  partyLeaders: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/kt3z5ocq_partyleaders.jpg',
};

const fmt = (n) => new Intl.NumberFormat('cs-CZ').format(n);

const ZlinerOfferPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [items, setItems] = useState({
    catering:    { selected: true, fixed: true,  price: 220000, name: 'Catering vč. zázemí a inventáře pro hosty (150 os)' },
    party:       { selected: true, fixed: false, price: 39000,  name: 'Kapela Párty Leaders (živé vystoupení 180 min.)' },
    quiz:        { selected: true, fixed: false, price: 15000,  name: 'Hospodský kvíz vč. moderátora a vyhlášení' },
    dj:          { selected: true, fixed: false, price: 15000,  name: 'DJ / moderátor' },
    audio_light: { selected: true, fixed: false, price: 15000,  name: 'Zvukař + základní osvětlení' },
    stage:       { selected: true, fixed: false, price: 25000,  name: 'Profesionální zastřešené pódium 8×6 m (pro kapelu)' },
    decoration:  { selected: true, fixed: false, price: 15000,  name: 'Vánoční výzdoba akce' },
    agency_fee:  { selected: true, fixed: true,  price: 10000,  name: 'Agenturní režie' },
  });

  const total = useMemo(
    () => Object.values(items).reduce((s, i) => s + (i.selected ? i.price : 0), 0),
    [items]
  );

  const toggle = (key) => setItems((prev) => (
    prev[key].fixed ? prev : { ...prev, [key]: { ...prev[key], selected: !prev[key].selected } }
  ));

  return (
    <div className="min-h-screen bg-[#04070d] text-slate-100 overflow-x-hidden" data-testid="zliner-offer-page">
      <Snowfall />
      <Hero />
      <Concept />
      <Timeline />
      <GallerySection />
      <MenuSection />
      <QuizSection />
      <BandSection />
      <Budget items={items} onToggle={toggle} total={total} />
      <Footer />
    </div>
  );
};

/* ============================= HERO ============================= */
const Hero = () => (
  <header className="relative overflow-hidden">
    {/* gradient stripes */}
    <div className="absolute inset-x-0 top-0 h-1 flex z-20">
      <div className="flex-1 bg-[#2AB7CA]" />
      <div className="flex-1 bg-[#8B3F9E]" />
      <div className="flex-1 bg-[#D4AF37]" />
    </div>
    {/* radial glow */}
    <div className="absolute inset-0 pointer-events-none opacity-70" style={{
      background: 'radial-gradient(60% 55% at 30% 25%, rgba(42,183,202,0.28), transparent 60%), radial-gradient(50% 50% at 80% 60%, rgba(139,63,158,0.30), transparent 65%)'
    }} />
    {/* bg photo */}
    <div className="absolute inset-0 -z-10">
      <img src={PHOTOS.tentInterior} alt="" className="w-full h-full object-cover opacity-35" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#04070d]/70 via-[#04070d]/70 to-[#04070d]" />
    </div>

    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-20 sm:pt-20 sm:pb-28">
      <div className="flex flex-col items-center text-center gap-5">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md px-4 py-1.5 text-[11px] tracking-[0.28em] uppercase text-white font-semibold">
          <Snowflake className="w-3.5 h-3.5 text-[#2AB7CA]" />
          Firemní vánoční akce · 2026
        </div>

        <div className="mt-2 rounded-2xl bg-white/95 px-6 py-4 shadow-xl">
          <img src={LOGO_ZLINER} alt="Zliner · Iveco Bus" className="h-14 sm:h-16 lg:h-20 w-auto" />
        </div>

        <h1 className="mt-4 font-black leading-[1.02] text-4xl sm:text-6xl lg:text-7xl text-white tracking-tight" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
          Vánoční akce <span className="bg-gradient-to-r from-[#2AB7CA] via-[#D4AF37] to-[#8B3F9E] bg-clip-text text-transparent">ZLINER</span>
        </h1>
        <div className="text-[#D4AF37] font-semibold tracking-[0.35em] text-xs sm:text-sm uppercase">Vánoční trhy · Poděkování zaměstnancům</div>

        <p className="max-w-2xl text-slate-300 text-base sm:text-lg mt-4">
          Firemní vánoční večer ve stylu tradičních trhů — s vůní svařáku, punče, grilem Ofyr, kapelou Párty Leaders a interaktivním hospodským kvízem.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8 w-full max-w-4xl">
          {[
            { icon: Calendar, label: '11. 12. 2026', sub: 'Pátek' },
            { icon: Clock,    label: '15:00 – 24:00', sub: 'Průběh akce' },
            { icon: Users,    label: '~ 150', sub: 'Zaměstnanců' },
            { icon: MapPin,   label: 'Areál Zliner', sub: 'Exteriér + interiér' },
          ].map((s, i) => (
            <div key={s.label} className="relative rounded-xl bg-white/5 border border-white/10 backdrop-blur-md p-4 text-left overflow-hidden group hover:border-[#D4AF37]/60 transition-colors" data-testid={`hero-stat-${i}`}>
              <s.icon className="w-5 h-5 text-[#D4AF37] mb-2" />
              <div className="text-white font-bold text-lg leading-tight">{s.label}</div>
              <div className="text-slate-400 text-xs mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </header>
);

/* ============================= CONCEPT ============================= */
const Concept = () => (
  <section className="relative py-16 sm:py-20 border-t border-white/5">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <SectionLabel icon={Sparkles} eyebrow="01 · Koncept akce" title="Vánoční trhy pro zaměstnance" />
      <div className="grid lg:grid-cols-5 gap-8 mt-10 items-start">
        <div className="lg:col-span-3 space-y-4 text-slate-300 leading-relaxed">
          <p className="text-lg">
            Poděkujte zaměstnancům za odvedenou práci <strong className="text-white">jedinečnou vánoční akcí</strong>, která spojuje atmosféru českých vánočních trhů s prémiovým firemním večerem.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-5 hover:border-[#2AB7CA]/50 transition-colors">
              <TentTree className="w-6 h-6 text-[#2AB7CA] mb-3" />
              <div className="text-white font-bold mb-1">Exteriér</div>
              <div className="text-sm text-slate-400">Vytápěné stany, plynové teplomety, vinné sudy jako stolky, gril Ofyr, food truck, bar truck s punčem a svařákem.</div>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-5 hover:border-[#8B3F9E]/50 transition-colors">
              <Trophy className="w-6 h-6 text-[#8B3F9E] mb-3" />
              <div className="text-white font-bold mb-1">Interiér</div>
              <div className="text-sm text-slate-400">Týmový <strong className="text-white">hospodský kvíz</strong> s moderátorem a vyhlášením vítězů, komfortní posezení, teplé zázemí pro rodinnou atmosféru.</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 rounded-2xl bg-gradient-to-br from-[#2AB7CA]/15 via-[#8B3F9E]/10 to-[#D4AF37]/15 border border-white/10 p-6">
          <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold mb-3">FIREMNÍ IDENTITA</div>
          <div className="text-white font-bold text-lg leading-snug mb-3">Laděno do barev Zliner &amp; IVECO Bus</div>
          <div className="text-sm text-slate-300 leading-relaxed">
            Výzdoba a doplňky respektují korporátní identitu klienta — tyrkysová, fialová a zlatá s decentními vánočními akcenty (jedle, světla, ozdoby, mandarinky).
          </div>
          <div className="mt-5 flex gap-2">
            <span className="w-8 h-8 rounded-full border border-white/20" style={{ background: '#2AB7CA' }} />
            <span className="w-8 h-8 rounded-full border border-white/20" style={{ background: '#8B3F9E' }} />
            <span className="w-8 h-8 rounded-full border border-white/20" style={{ background: '#D4AF37' }} />
            <span className="w-8 h-8 rounded-full border border-white/20" style={{ background: '#0b3b2a' }} />
            <span className="w-8 h-8 rounded-full border border-white/20" style={{ background: '#a5232f' }} />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============================= TIMELINE ============================= */
const Timeline = () => {
  const slots = [
    { time: '15:00',       title: 'Zahájení a welcome',            desc: 'Oficiální přivítání hostů, otevření areálu, start programu.',                                                 icon: Sparkles,  color: '#D4AF37', zone: 'EXTERIÉR' },
    { time: '15:00–17:00', title: 'Podkresová vánoční hudba',      desc: 'Jemná akustická hudba v pozadí u stánků a food trucku navodí atmosféru vánočních trhů.',                      icon: Music4,    color: '#2AB7CA', zone: 'EXTERIÉR' },
    { time: '15:00–24:00', title: 'Vánoční trhy',                  desc: 'Volný pohyb po areálu — gril Ofyr, food truck, bar truck, tradiční sortiment.',                                icon: TentTree,  color: '#D4AF37', zone: 'EXTERIÉR' },
    { time: '15:30',       title: 'Welcome punč a svařák',         desc: 'Přivítání hostů horkým punčem a svařákem u vánočních stánků.',                                                icon: Wine,      color: '#a5232f', zone: 'EXTERIÉR' },
    { time: '15:45–18:15', title: 'Hospodský kvíz + vyhlášení',    desc: 'Týmový kvíz s moderátorem, projekcí otázek a vyhlášením vítězů (ceny dodá Zliner).',                           icon: Trophy,    color: '#8B3F9E', zone: 'INTERIÉR' },
    { time: '17:30–20:30', title: 'Kapela Párty Leaders',          desc: 'Živé vystoupení oblíbené české party kapely — hity od 70. let po současnost (180 min.).',                     icon: Music4,    color: '#2AB7CA', zone: 'EXTERIÉR' },
    { time: '20:30–24:00', title: 'DJ set',                        desc: 'Taneční vrchol večera — DJ pod vánočním nebem až do půlnoci.',                                                 icon: Flame,     color: '#D4AF37', zone: 'EXTERIÉR' },
  ];
  return (
    <section className="relative py-16 sm:py-20 border-t border-white/5 bg-gradient-to-b from-transparent to-white/[0.02]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionLabel icon={Clock} eyebrow="02 · Program" title="Časová osa dne" />

        <div className="mt-6 flex flex-wrap gap-3 text-[11px] font-bold tracking-[0.22em] uppercase">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#2AB7CA]/40 bg-[#2AB7CA]/10 text-[#8ad8e3] px-3 py-1.5">
            <TentTree className="w-3.5 h-3.5" /> Exteriér
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#8B3F9E]/40 bg-[#8B3F9E]/10 text-[#c9a0d6] px-3 py-1.5">
            <Trophy className="w-3.5 h-3.5" /> Interiér
          </span>
        </div>

        <ol className="mt-10 relative before:absolute before:left-4 sm:before:left-1/2 before:top-2 before:bottom-2 before:w-px before:bg-gradient-to-b before:from-[#2AB7CA] before:via-[#8B3F9E] before:to-[#D4AF37]">
          {slots.map((s, i) => (
            <li key={`${s.time}-${s.title}`} className={`relative sm:grid sm:grid-cols-2 gap-8 mb-8 sm:mb-10 ${i % 2 ? 'sm:[&>div:first-child]:col-start-2' : ''}`}>
              <div className={`pl-12 sm:pl-0 ${i % 2 ? 'sm:pl-8 sm:text-left' : 'sm:pr-8 sm:text-right'}`}>
                <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 hover:border-white/25 transition-colors">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[11px] tracking-[0.3em] font-bold uppercase" style={{ color: s.color }}>{s.time}</div>
                    <span className={`text-[9px] font-bold tracking-[0.22em] uppercase px-2 py-0.5 rounded-full border ${
                      s.zone === 'INTERIÉR'
                        ? 'border-[#8B3F9E]/50 bg-[#8B3F9E]/15 text-[#d7b7e3]'
                        : 'border-[#2AB7CA]/50 bg-[#2AB7CA]/15 text-[#9ad9e3]'
                    }`}>{s.zone}</span>
                  </div>
                  <div className="text-white font-bold text-lg mt-1.5">{s.title}</div>
                  <div className="text-sm text-slate-400 mt-2 leading-relaxed">{s.desc}</div>
                </div>
              </div>
              <div className="absolute left-0 sm:left-1/2 top-2 sm:-translate-x-1/2 w-8 h-8 rounded-full border-2 border-[#04070d] flex items-center justify-center shadow-lg" style={{ backgroundColor: s.color }}>
                <s.icon className="w-4 h-4 text-white" />
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ============================= GALLERY ============================= */
const GallerySection = () => (
  <section className="relative py-16 sm:py-20 border-t border-white/5">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <SectionLabel icon={TentTree} eyebrow="03 · Exteriér" title="Atmosféra vánočních trhů" />
      <p className="max-w-2xl text-slate-400 mt-4 text-base leading-relaxed">
        Prostor obsazený stany s teplomety, decentním osvětlením, vánočními stromky, ovčími kůžemi na sezení, sudovými stolky a otevřeným ohněm z grilu Ofyr.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mt-10">
        <GalleryCard src={PHOTOS.ofyrNight}     title="Vánoční trhy po setmění" subtitle="Nasvícené stánky, dřevěné doplňky a teplo ohně" />
        <GalleryCard src={PHOTOS.tentsDay}      title="Zastřešené posezení"     subtitle="Vytápěné stany, lavice, dřevěné sudy k postávání" />
        <GalleryCard src={PHOTOS.tentInterior}  title="Vánoční výzdoba"         subtitle="Jedličky, teplé světlo, přírodní materiály" />
        <GalleryCard src={PHOTOS.foodTruck}     title="Food truck a stánky"     subtitle="Vánoční gastronomie na dosah ruky" />
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Flame,           label: 'Gril Ofyr' },
          { icon: UtensilsCrossed, label: 'Food truck' },
          { icon: Wine,            label: 'Bar truck · punč · svařák' },
          { icon: TentTree,        label: 'Stany + teplomety' },
        ].map((f) => (
          <div key={f.label} className="rounded-xl bg-white/[0.03] border border-white/10 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
              <f.icon className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div className="text-sm text-white font-semibold">{f.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const GalleryCard = ({ src, title, subtitle }) => (
  <figure className="group relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]">
    <div className="aspect-[4/3] overflow-hidden">
      <img src={src} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
    <figcaption className="absolute bottom-0 left-0 right-0 p-5">
      <div className="text-white font-bold text-base sm:text-lg">{title}</div>
      <div className="text-slate-300 text-xs sm:text-sm mt-0.5">{subtitle}</div>
    </figcaption>
  </figure>
);

/* ============================= MENU ============================= */
const MENU = {
  cold: {
    label: 'Studené pokrmy',
    icon: Snowflake,
    accent: '#2AB7CA',
    items: [
      'Husí paštika s brusinkami na čerstvém chlebu s cibulovým chutney',
      'Nakládané tvarůžky s máslem a feferonkou na domácím chlebu',
      'Vlčnovské koláčky',
      'Mandlový řez s lesním ovocem',
      'Jablečný štrúdl',
      'Vánoční cukroví',
      'Ovoce',
    ],
  },
  warm: {
    label: 'Teplý raut',
    icon: Flame,
    accent: '#D4AF37',
    items: [
      'Tradiční vánoční zelňačka s klobáskou a bramborem',
      'Smažený kuřecí řízek s bramborovým salátem a citrónem',
      'Jelení guláš s cibulkou, feferonkou a petrželkou, kváskový chléb',
      'Grilovaná vepřová kýta na ohni s křenem, hořčicí a kysaným zelím',
      'Grilovaná klobáska s křenem, hořčicí a kysaným zelím',
      'Grilovaný uzený oštiepok s brusinkami',
      'Läberkase sandwich s medovou hořčicí a kysaným zelím',
    ],
  },
  drinks: {
    label: 'Nápoje',
    icon: Wine,
    accent: '#8B3F9E',
    items: [
      'Horká hruška',
      'Nealkoholický svařák',
      'Svařák',
      'Degustace rumů',
      'Vánoční limonáda – jablko se skořicí',
      'Voda',
      'Birell',
      'Čaj',
      'Nespresso',
      'Pivo',
    ],
  },
};

const MenuSection = () => (
  <section className="relative py-16 sm:py-20 border-t border-white/5 bg-gradient-to-b from-transparent via-[#D4AF37]/[0.04] to-transparent">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <SectionLabel icon={UtensilsCrossed} eyebrow="04 · Catering" title="Vánoční menu" />
        <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/15 to-transparent px-5 py-4 shrink-0">
          <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">CENA ZA CATERING</div>
          <div className="text-white font-black text-2xl mt-1 tabular-nums">
            {fmt(220000)} <span className="text-sm text-[#D4AF37] font-semibold">Kč bez DPH</span>
          </div>
          <div className="text-xs text-slate-400 mt-0.5">pro 150 osob</div>
        </div>
      </div>

      <p className="max-w-2xl text-slate-400 mt-4 text-base leading-relaxed">
        Tradiční česká vánoční kuchyně, grilované speciality z Ofyr a bar s punčem i degustací rumů. Vše servírováno v atmosféře vánočních trhů.
      </p>

      {/* Video preview */}
      <div className="grid lg:grid-cols-5 gap-5 mt-8 items-stretch">
        <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-white/10 bg-black/50">
          <div className="relative pb-[177.78%] h-0 overflow-hidden">
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/N-Nt7AgYXSA"
              title="Ukázka cateringu – Vše Pro Akci"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
          <div className="p-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">UKÁZKA Z REALIZACE</div>
              <div className="text-white font-bold text-sm mt-0.5">Catering v praxi</div>
            </div>
            <a
              href="https://www.youtube.com/shorts/N-Nt7AgYXSA"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-300 hover:text-[#D4AF37] shrink-0"
            >
              YouTube ↗
            </a>
          </div>
        </div>

        <div className="lg:col-span-3 relative rounded-2xl overflow-hidden border border-white/10 flex flex-col justify-end min-h-[420px]">
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_vsepro-wanzl/artifacts/b6izf6io_imrvere.webp"
            alt="Šéfkuchař s vánočními pokrmy"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#04070d] via-[#04070d]/85 to-[#04070d]/20" />
          <div className="relative p-6 sm:p-7">
            <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold mb-3">O NAŠEM VÁNOČNÍM CATERINGU</div>
            <h3 className="text-white font-black text-2xl sm:text-3xl leading-snug tracking-tight">Grilované speciality, teplý raut i tradiční cukroví</h3>
            <p className="text-slate-200 text-sm mt-3 leading-relaxed max-w-xl">
              Catering realizujeme v otevřené kuchyni přímo před hosty — grilujeme na dřevěném uhlí a Ofyr. Zázemí a inventář pro 150 osob zajistíme my: vytápěné stany, bistro stolky, sudy k postávání a obsluhu.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mt-5">
              {[
                'Otevřená kuchyně před hosty',
                'Čerstvé suroviny, místní dodavatelé',
                'Obsluha v jednotném vánočním stylu',
                'Kompletní zázemí a inventář v ceně',
              ].map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-slate-100">
                  <Check className="w-4 h-4 mt-0.5 text-[#D4AF37] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mt-10">
        {Object.entries(MENU).map(([key, cat]) => (
          <article key={key} data-testid={`menu-card-${key}`} className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-6 overflow-hidden group hover:border-white/25 transition-colors">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-20" style={{ background: cat.accent }} />
            <div className="relative flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${cat.accent}22`, border: `1px solid ${cat.accent}55` }}>
                <cat.icon className="w-5 h-5" style={{ color: cat.accent }} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.28em] font-bold uppercase" style={{ color: cat.accent }}>{cat.label}</div>
                <div className="text-white font-black text-xl leading-tight mt-0.5">{cat.items.length} položek</div>
              </div>
            </div>
            <ul className="relative space-y-2.5">
              {cat.items.map((it, i) => (
                <li key={`${key}-${i}`} className="flex items-start gap-2.5 text-sm text-slate-300 leading-snug">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: cat.accent }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ============================= QUIZ ============================= */
const QuizSection = () => (
  <section className="relative py-16 sm:py-20 border-t border-white/5 bg-gradient-to-b from-transparent to-[#8B3F9E]/[0.05]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-8 items-center">
      <div className="lg:col-span-3">
        <SectionLabel icon={Trophy} eyebrow="05 · Interiér" title="Hospodský kvíz + vyhlášení" />
        <p className="text-slate-300 leading-relaxed mt-6">
          V interiéru připravíme <strong className="text-white">týmový hospodský kvíz</strong> s moderátorem, projekcí otázek a živým skórováním. Zaměstnanci se rozdělí do týmů a soutěží v otázkách o firmě, Vánocích, hudbě a filmu. Čas: <strong className="text-white">15:45 – 18:15</strong>.
        </p>
        <ul className="mt-6 space-y-3">
          {[
            'Moderátor + technické zajištění (mikrofon, projektor, ozvučení)',
            '5 kol × 8 otázek + bonusová disciplína',
            'Živé skórování na projekci',
            'Slavnostní vyhlášení a předání cen vítězům',
          ].map((it) => (
            <li key={it} className="flex items-start gap-3 text-slate-300 text-sm">
              <Check className="w-4 h-4 mt-0.5 text-[#D4AF37] shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="lg:col-span-2">
        <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#8B3F9E]/20 via-[#a5232f]/15 to-[#D4AF37]/20 p-8 text-center">
          <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold mb-2">CENA ZA REALIZACI KVÍZU</div>
          <div className="font-black text-white text-5xl sm:text-6xl leading-none tabular-nums" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
            15&nbsp;000
          </div>
          <div className="text-[#D4AF37] font-bold text-lg mt-1">Kč bez DPH</div>
          <div className="mt-4 text-sm text-slate-300 leading-relaxed">moderátor · technika · otázky · vyhlášení</div>
          <div className="mt-5 text-xs rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-slate-300">
            <strong className="text-white">Ceny pro vítěze</strong> dodá klient (Zliner).
          </div>
          <Trophy className="w-9 h-9 text-[#D4AF37] mx-auto mt-6" />
        </div>
      </div>
    </div>
  </section>
);

/* ============================= BAND (Párty Leaders) ============================= */
const BandSection = () => (
  <section className="relative py-16 sm:py-20 border-t border-white/5">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <SectionLabel icon={Music4} eyebrow="06 · Hudební program" title="Kapela Párty Leaders + pódium" />
      <p className="max-w-2xl text-slate-400 mt-4 leading-relaxed">
        Oblíbená party kapela, která zvedne vaše hosty ze židlí — repertoár od <strong className="text-white">70. let až po současné hity</strong>. 180 min živého hraní na profesionálním zastřešeném pódiu 8×6 m s plným zvukem a světelným parkem.
      </p>

      {/* Video + Party Leaders */}
      <div className="grid lg:grid-cols-5 gap-5 mt-10">
        <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-white/10 bg-black/40">
          <div className="relative pb-[56.25%] h-0 overflow-hidden">
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src="https://www.youtube.com/embed/fP87mouxM28"
              title="Párty Leaders – Silvestr 2024 v Hilton Prague"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
          <div className="p-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">UKÁZKA ŽIVÉHO VYSTOUPENÍ</div>
              <div className="text-white font-bold text-sm mt-0.5">Párty Leaders — Hilton Prague</div>
            </div>
            <a
              href="https://www.youtube.com/watch?v=fP87mouxM28"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-300 hover:text-[#D4AF37]"
            >
              Otevřít na YouTube ↗
            </a>
          </div>
        </div>

        <figure className="lg:col-span-2 relative rounded-2xl overflow-hidden border border-white/10">
          <div className="aspect-[4/5] lg:h-full overflow-hidden">
            <img src={PHOTOS.partyLeaders} alt="Kapela Párty Leaders" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4">
            <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">KAPELA</div>
            <div className="text-white font-bold text-base mt-0.5">Párty Leaders · živé vystoupení</div>
          </figcaption>
        </figure>
      </div>

      {/* Pódium – velké fotky */}
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <figure className="relative rounded-2xl overflow-hidden border border-white/10 group">
          <div className="aspect-[16/10] overflow-hidden">
            <img src={PHOTOS.stage1} alt="Zastřešené pódium 8×6 m" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <figcaption className="absolute inset-x-0 bottom-0 p-5">
            <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">PÓDIUM</div>
            <div className="text-white font-bold text-lg mt-0.5">Zastřešené pódium 8 × 6 m</div>
            <div className="text-slate-300 text-xs mt-1">Profesionální konstrukce, zadní a boční stěny</div>
          </figcaption>
        </figure>
        <figure className="relative rounded-2xl overflow-hidden border border-white/10 group">
          <div className="aspect-[16/10] overflow-hidden">
            <img src={PHOTOS.stage2} alt="Pódium v akci na firemní akci" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <figcaption className="absolute inset-x-0 bottom-0 p-5">
            <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">V AKCI</div>
            <div className="text-white font-bold text-lg mt-0.5">Realizace na firemní akci</div>
            <div className="text-slate-300 text-xs mt-1">Doprava, montáž a demontáž v ceně</div>
          </figcaption>
        </figure>
      </div>

      {/* Cenová tabulka */}
      <div className="grid md:grid-cols-4 gap-4 mt-8">
        {[
          { label: 'Kapela Párty Leaders', sub: 'Živé vystoupení 180 min.', price: 39000, color: '#2AB7CA' },
          { label: 'Pódium 8×6 m',          sub: 'Profesionální zastřešené', price: 25000, color: '#D4AF37' },
          { label: 'Zvukař + osvětlení',    sub: 'Základní zvuk a světlo',   price: 15000, color: '#8B3F9E' },
          { label: 'DJ / moderátor',        sub: 'Doznění večera do půlnoci', price: 15000, color: '#a5232f' },
        ].map((c) => (
          <div key={c.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="text-[10px] tracking-[0.28em] font-bold uppercase" style={{ color: c.color }}>{c.label}</div>
            <div className="text-slate-400 text-xs mt-1">{c.sub}</div>
            <div className="text-2xl font-black text-white mt-3 tabular-nums">
              {fmt(c.price)} <span className="text-xs text-slate-400 font-semibold">Kč</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================= BUDGET ============================= */
const Budget = ({ items, onToggle, total }) => (
  <section className="relative py-16 sm:py-24 border-t border-white/5 bg-gradient-to-b from-transparent via-[#2AB7CA]/[0.04] to-transparent" id="rozpocet">
    <div className="max-w-5xl mx-auto px-4 sm:px-6">
      <SectionLabel icon={Star} eyebrow="07 · Rozpočet" title="Interaktivní kalkulace" />
      <p className="max-w-2xl text-slate-400 mt-4 leading-relaxed">
        Pevné položky jsou nezbytné pro realizaci akce. <strong className="text-white">Volitelné položky můžete odkliknout</strong> a celková cena se automaticky přepočítá.
      </p>

      <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md overflow-hidden">
        <div className="divide-y divide-white/5">
          {Object.entries(items).map(([key, it]) => (
            <BudgetRow key={key} testKey={key} item={it} onToggle={() => onToggle(key)} />
          ))}
        </div>
        <div className="bg-gradient-to-r from-[#2AB7CA]/20 via-[#8B3F9E]/25 to-[#D4AF37]/20 p-6 sm:p-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="text-[11px] tracking-[0.3em] text-[#D4AF37] font-bold">CELKEM BEZ DPH</div>
              <div className="text-slate-300 text-xs mt-1">*Ceny pro vítěze hospodského kvízu dodá klient (Zliner)</div>
            </div>
            <div className="text-4xl sm:text-5xl font-black text-white tracking-tight" data-testid="budget-total">
              {fmt(total)} <span className="text-lg text-[#D4AF37] font-semibold">Kč</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const BudgetRow = ({ testKey, item, onToggle }) => {
  const disabled = item.fixed;
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      data-testid={`budget-row-${testKey}`}
      className={`w-full text-left flex items-center gap-4 p-5 transition-colors ${
        disabled ? 'cursor-default' : 'cursor-pointer hover:bg-white/[0.04]'
      }`}
    >
      <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${
        item.selected
          ? disabled ? 'bg-[#D4AF37] border-[#D4AF37]' : 'bg-[#2AB7CA] border-[#2AB7CA]'
          : 'bg-transparent border-slate-500'
      }`}>
        {item.selected && <Check className="w-4 h-4 text-slate-900" strokeWidth={3} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold text-base sm:text-[15px] leading-tight">{item.name}</div>
        <div className="mt-1 flex items-center gap-2">
          {disabled ? (
            <span className="text-[10px] tracking-widest text-[#D4AF37] font-bold uppercase">Pevná položka</span>
          ) : (
            <span className="text-[10px] tracking-widest text-[#2AB7CA] font-bold uppercase">Volitelné · lze odkliknout</span>
          )}
        </div>
      </div>
      <div className={`text-lg sm:text-xl font-black shrink-0 tabular-nums ${item.selected ? 'text-white' : 'text-slate-500 line-through'}`}>
        {fmt(item.price)} <span className="text-xs font-semibold text-slate-400">Kč</span>
      </div>
    </button>
  );
};

/* ============================= FOOTER ============================= */
const Footer = () => (
  <footer className="relative border-t border-white/10 bg-gradient-to-b from-transparent to-black/60 py-12 mt-8">
    <div className="absolute inset-x-0 bottom-0 h-1 flex">
      <div className="flex-1 bg-[#D4AF37]" />
      <div className="flex-1 bg-[#8B3F9E]" />
      <div className="flex-1 bg-[#2AB7CA]" />
    </div>
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="grid md:grid-cols-3 gap-8 items-start">
        <div className="md:col-span-2">
          <img src={LOGO_VPA} alt="Vše Pro Akci" className="h-10 w-auto opacity-90" />
          <div className="mt-4 text-white font-bold text-lg">Vše Pro Akci s.r.o.</div>
          <div className="text-slate-400 text-sm mt-2 leading-relaxed max-w-md">
            Kompletní produkce firemních eventů — od konceptu přes catering a techniku až po realizaci a úklid. Nabídka je platná 30 dnů od data odeslání.
          </div>
        </div>
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
          <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold mb-3">KONTAKT</div>
          <div className="text-white font-bold">Jakub Vodárek</div>
          <div className="text-slate-400 text-sm">Jednatel</div>
          <a href="tel:+420774750456" className="mt-3 block text-white hover:text-[#D4AF37] text-sm">+420 774 750 456</a>
          <a href="mailto:info@vseproakci.cz" className="text-white hover:text-[#D4AF37] text-sm">info@vseproakci.cz</a>
        </div>
      </div>
      <div className="mt-10 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
        <div>© {new Date().getFullYear()} Vše Pro Akci s.r.o. · Nabídka pro Zliner</div>
        <div>Ceny uvedeny bez DPH.</div>
      </div>
    </div>
  </footer>
);

/* ============================= HELPERS ============================= */
const SectionLabel = ({ icon: Icon, eyebrow, title }) => (
  <div>
    <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-bold text-[#D4AF37]">
      <Icon className="w-4 h-4" />
      {eyebrow}
    </div>
    <h2 className="mt-3 font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
      {title}
    </h2>
  </div>
);

const Snowfall = () => (
  <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
    {Array.from({ length: 24 }).map((_, i) => {
      const left = (i * 37) % 100;
      const delay = (i * 0.7) % 10;
      const duration = 12 + ((i * 3) % 10);
      const size = 6 + (i % 4) * 2;
      return (
        <span
          key={`snow-${i}`}
          className="absolute -top-4 text-white/25"
          style={{
            left: `${left}%`,
            fontSize: `${size}px`,
            animation: `snowfall ${duration}s linear ${delay}s infinite`,
          }}
        >
          ❄
        </span>
      );
    })}
    <style>{`
      @keyframes snowfall {
        0%   { transform: translate3d(0, -10vh, 0) rotate(0deg);   opacity: 0; }
        10%  { opacity: 0.9; }
        90%  { opacity: 0.6; }
        100% { transform: translate3d(20px, 110vh, 0) rotate(180deg); opacity: 0; }
      }
    `}</style>
  </div>
);

export default ZlinerOfferPage;
